import {ListItemButton, ListItemIcon, Tooltip} from "@mui/material";
import {LogoutOutlined} from "@mui/icons-material";
import ListItemText from "@mui/material/ListItemText/ListItemText";
import React, {useContext} from "react";
import {LanguageContext} from "../../config/ContextConfig";
import {useDispatch, useSelector} from "react-redux";
import {userLogout} from "../../actions/common/AuthenticatedUserActions";
import {VisibilityHandler} from "../common/VisibilityHandler";
import {AppState} from "../../reducers/RootReducer";
import {UnsavedDataProtection} from "../common/dialogs/UnsavedDataProtection";
import {useVisibility} from "../../config/hooks/useToggle";

export interface NavDrawerLogoutItemProps {
    hideText?: boolean;
}

/**
 * Renders a navigation drawer logout item that allows the user to safely log out of the application.
 * Provides optional protection against unsaved data loss when logging out.
 *
 * This component integrates with user interface and state management tools to offer a responsive and user-friendly logout experience.
 * If unsaved data protection is enabled, it prompts the user with a confirmation dialog before proceeding with the logout workflow.
 *
 * @param props - Properties used to configure the behavior and appearance of the logout item.
 * @param props.hideText - A boolean that determines whether the text label is hidden. When true, only the logout icon is displayed, and the component is positioned as a fixed element.
 *
 * @returns A React element representing the navigation drawer logout item. It includes an optional confirmation dialog for unsaved data protection.
 *
 * @throws Error if the required context or state hooks (e.g., `LanguageContext`, `ViewSettingsReducer`) are not properly initialized or if a dispatch action fails to trigger.
 */
export function NavDrawerLogoutItem(props: NavDrawerLogoutItemProps) {

    const language = useContext( LanguageContext );
    const viewSettingsState = useSelector( ( appState: AppState) => appState.ViewSettingsReducer );
    const [ confirmVisible, { toggleVisibility } ] = useVisibility();
    const dispatch = useDispatch();

    return (
        <div
            style={{
                borderLeft: "8px solid white",
                position: props.hideText ? 'fixed' : 'relative',
                bottom: props.hideText ? 5+'px' : 'auto'
            }}
            onClick={() => {
                if( viewSettingsState.unsavedDataProtection ) {
                    toggleVisibility();
                } else {
                    dispatch( userLogout() );
                }
            }}
        >
            {
                viewSettingsState.unsavedDataProtection
                ? (
                        <UnsavedDataProtection
                            isOpen={ confirmVisible }
                            onYesClick={ () => dispatch( userLogout() ) }
                            onNoClick={ toggleVisibility }
                        />
                    )
                : null
            }
            <Tooltip title={language.userManagment.logout}  placement={'right-end'}>
                <ListItemButton style={{display: "flex", maxWidth: 62, height: '60px'}} className={"nav-drawer-background menu-border-hidden"}>
                    <ListItemIcon>
                        <LogoutOutlined className="icon" />
                    </ListItemIcon>
                    <VisibilityHandler isVisible={ ! props.hideText ?? true }>
                        <ListItemText primary={ language.userManagment.logout } style={{color: 'black'}}/>
                    </VisibilityHandler>

                </ListItemButton>
            </Tooltip>
        </div>
    )

}